/**
 * Gregorian dates ⇄ Japanese era (nengō) dates, Meiji to Reiwa.
 *
 * Era start dates (Gregorian):
 *
 * - Meiji    1868-10-23  (the era name was changed on 8 September Keiō 4 in the lunisolar
 *            calendar; Keiō 4 was renamed Meiji 1, retroactive to its New Year)
 * - Taishō   1912-07-30  (the Meiji Emperor died)
 * - Shōwa    1926-12-25  (the Taishō Emperor died)
 * - Heisei   1989-01-08  (the Shōwa Emperor died on 7 January)
 * - Reiwa    2019-05-01  (accession of Emperor Naruhito)
 *
 * Counting: the first year of an era (gannen, 元年) runs from the start date to 31 December.
 * From the next 1 January each Gregorian year adds one, so the era year is the Gregorian year
 * minus a fixed offset, and a Gregorian year that holds an era change belongs to two eras.
 *
 * Caveat: Japan used a lunisolar calendar until 1 January 1873 (Meiji 6), which followed
 * 2 December Meiji 5 in the old calendar. Gregorian dates before 1873 therefore give an
 * approximate era year: Meiji years 1–5 began at the old New Year, in January or February.
 */

export type EraId = "meiji" | "taisho" | "showa" | "heisei" | "reiwa";

export interface Era {
  id: EraId;
  kanji: string;
  romaji: string;
  /** One-letter abbreviation used in forms and filenames. */
  letter: string;
  /** The single-character compatibility glyph (㍾ ㍽ ㍼ ㍻ ㋿). */
  square: string;
  /** Gregorian year of era year 0, so era year = Gregorian year − offset. */
  offset: number;
  start: DayOfYear;
}

export interface DayOfYear {
  year: number;
  month: number;
  day: number;
}

export const ERAS: readonly Era[] = [
  {
    id: "meiji",
    kanji: "明治",
    romaji: "Meiji",
    letter: "M",
    square: "㍾",
    offset: 1867,
    start: { year: 1868, month: 10, day: 23 },
  },
  {
    id: "taisho",
    kanji: "大正",
    romaji: "Taishō",
    letter: "T",
    square: "㍽",
    offset: 1911,
    start: { year: 1912, month: 7, day: 30 },
  },
  {
    id: "showa",
    kanji: "昭和",
    romaji: "Shōwa",
    letter: "S",
    square: "㍼",
    offset: 1925,
    start: { year: 1926, month: 12, day: 25 },
  },
  {
    id: "heisei",
    kanji: "平成",
    romaji: "Heisei",
    letter: "H",
    square: "㍻",
    offset: 1988,
    start: { year: 1989, month: 1, day: 8 },
  },
  {
    id: "reiwa",
    kanji: "令和",
    romaji: "Reiwa",
    letter: "R",
    square: "㋿",
    offset: 2018,
    start: { year: 2019, month: 5, day: 1 },
  },
];

/** The first Gregorian day in Japan's calendar: Meiji 6, 1 January. */
export const GREGORIAN_ADOPTED: DayOfYear = { year: 1873, month: 1, day: 1 };

export const MAX_GREGORIAN_YEAR = 9999;

export function eraById(id: EraId): Era {
  return ERAS.find((era) => era.id === id) ?? ERAS[0];
}

const key = (d: DayOfYear) => d.year * 10_000 + d.month * 100 + d.day;

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function daysInMonth(year: number, month: number): number {
  return month === 2 && isLeapYear(year) ? 29 : MONTH_DAYS[month - 1];
}

export function isValidDate(d: DayOfYear): boolean {
  return (
    Number.isInteger(d.year) &&
    Number.isInteger(d.month) &&
    Number.isInteger(d.day) &&
    d.year >= 1 &&
    d.year <= MAX_GREGORIAN_YEAR &&
    d.month >= 1 &&
    d.month <= 12 &&
    d.day >= 1 &&
    d.day <= daysInMonth(d.year, d.month)
  );
}

export function toIso(d: DayOfYear): string {
  const p = (n: number, w: number) => String(n).padStart(w, "0");
  return `${p(d.year, 4)}-${p(d.month, 2)}-${p(d.day, 2)}`;
}

/** The day before a date. */
function dayBefore(d: DayOfYear): DayOfYear {
  if (d.day > 1) {
    return { ...d, day: d.day - 1 };
  }
  if (d.month > 1) {
    return {
      year: d.year,
      month: d.month - 1,
      day: daysInMonth(d.year, d.month - 1),
    };
  }
  return { year: d.year - 1, month: 12, day: 31 };
}

/** The last day of an era, or null for the current one. */
export function eraEnd(era: Era): DayOfYear | null {
  const index = ERAS.indexOf(era);
  const next = ERAS[index + 1];
  return next ? dayBefore(next.start) : null;
}

export type EraNote =
  | "gannen"
  | "eraStart"
  | "eraEnd"
  | "sharedYear"
  | "lunisolar"
  | "firstGregorian";

export interface EraDate {
  era: Era;
  /** The year within the era: 1 is gannen (元年). */
  year: number;
  date: DayOfYear;
  /** True before 1873, when the era year is only approximate. */
  approximate: boolean;
  notes: EraNote[];
}

export type EraError = "invalid" | "beforeMeiji";

/** The era and era year for a Gregorian date. */
export function toEra(date: DayOfYear): EraDate | EraError {
  if (!isValidDate(date)) {
    return "invalid";
  }
  let era: Era | null = null;
  for (const candidate of ERAS) {
    if (key(candidate.start) <= key(date)) {
      era = candidate;
    }
  }
  if (era === null) {
    return "beforeMeiji";
  }
  const year = date.year - era.offset;
  const end = eraEnd(era);
  const notes: EraNote[] = [];
  if (year === 1) {
    notes.push("gannen");
  }
  if (key(era.start) === key(date)) {
    notes.push("eraStart");
  }
  if (end !== null && key(end) === key(date)) {
    notes.push("eraEnd");
  }
  if (ERAS.some((e) => e.start.year === date.year && e !== ERAS[0])) {
    notes.push("sharedYear");
  }
  const approximate = key(date) < key(GREGORIAN_ADOPTED);
  if (approximate) {
    notes.push("lunisolar");
  }
  if (key(date) === key(GREGORIAN_ADOPTED)) {
    notes.push("firstGregorian");
  }
  return { era, year, date, approximate, notes };
}

export interface EraSpan {
  era: Era;
  year: number;
  from: DayOfYear;
  to: DayOfYear;
}

/** Which era years a whole Gregorian year belongs to (two when an era changed in it). */
export function eraYearsOf(year: number): EraSpan[] | EraError {
  if (!Number.isInteger(year) || year < 1 || year > MAX_GREGORIAN_YEAR) {
    return "invalid";
  }
  if (year < ERAS[0].start.year) {
    return "beforeMeiji";
  }
  const first: DayOfYear = { year, month: 1, day: 1 };
  const last: DayOfYear = { year, month: 12, day: 31 };
  const spans: EraSpan[] = [];
  for (const era of ERAS) {
    const end = eraEnd(era);
    const from = key(era.start) > key(first) ? era.start : first;
    const to = end !== null && key(end) < key(last) ? end : last;
    if (key(from) <= key(to) && from.year === year && to.year === year) {
      spans.push({ era, year: year - era.offset, from, to });
    }
  }
  return spans;
}

export type FromEraError = "invalid" | "outsideEra";

export interface FromEra {
  era: Era;
  year: number;
  gregorianYear: number;
  /** The days of that Gregorian year the era year covers. */
  from: DayOfYear;
  to: DayOfYear;
  /** The exact date, when a month and day were given. */
  date: DayOfYear | null;
}

/** The last era year of a finished era (Heisei 31), or null for the current era. */
export function eraLastYear(era: Era): number | null {
  const end = eraEnd(era);
  return end === null ? null : end.year - era.offset;
}

/**
 * The days of the Gregorian year that one era year covers, or null when the era never had
 * that year. The first year of an era starts on the era's first day and the last year of a
 * finished era ends the day before the next era, so Heisei 31 ends on 30 April 2019.
 */
export function eraYearSpan(
  era: Era,
  year: number
): { from: DayOfYear; to: DayOfYear } | null {
  const last = eraLastYear(era);
  if (!Number.isInteger(year) || year < 1 || (last !== null && year > last)) {
    return null;
  }
  const gregorianYear = year + era.offset;
  const end = eraEnd(era);
  return {
    from: year === 1 ? era.start : { year: gregorianYear, month: 1, day: 1 },
    to:
      end !== null && end.year === gregorianYear
        ? end
        : { year: gregorianYear, month: 12, day: 31 },
  };
}

/**
 * Era year (and optionally month and day) to the Gregorian calendar. "invalid" is an
 * impossible date or year; "outsideEra" is a real one the era never contained.
 */
export function fromEra(
  eraId: EraId,
  year: number,
  month: number | null,
  day: number | null
): FromEra | FromEraError {
  const era = eraById(eraId);
  const gregorianYear = year + era.offset;
  if (
    !Number.isInteger(year) ||
    year < 1 ||
    gregorianYear > MAX_GREGORIAN_YEAR
  ) {
    return "invalid";
  }
  const span = eraYearSpan(era, year);
  if (span === null) {
    return "outsideEra";
  }
  const { from, to } = span;
  if (month === null || day === null) {
    return { era, year, gregorianYear, from, to, date: null };
  }
  const date: DayOfYear = { year: gregorianYear, month, day };
  if (!isValidDate(date)) {
    return "invalid";
  }
  if (key(date) < key(from) || key(date) > key(to)) {
    return "outsideEra";
  }
  return { era, year, gregorianYear, from, to, date };
}

/** 令和元年 or 令和8年: the era name and year in kanji. */
export function eraYearKanji(era: Era, year: number, gannen: boolean): string {
  return `${era.kanji}${year === 1 && gannen ? "元" : year}年`;
}

/** The full Japanese date: 令和元年5月1日. */
export function eraDateKanji(
  era: Era,
  year: number,
  date: DayOfYear,
  gannen: boolean
): string {
  return `${eraYearKanji(era, year, gannen)}${date.month}月${date.day}日`;
}

/** The compact form used on forms: R1.5.1 */
export function eraDateShort(era: Era, year: number, date: DayOfYear): string {
  return `${era.letter}${year}.${date.month}.${date.day}`;
}

/** The single-glyph form: ㋿1.5.1 */
export function eraDateSquare(era: Era, year: number, date: DayOfYear): string {
  return `${era.square}${year}.${date.month}.${date.day}`;
}
